import { RouteComponentProps } from 'react-router';
import { createSelector } from 'reselect';

import RootState from 'Store/Root';

import { SiteLink } from 'Types/Layout';

import {
  TryksByOrderState,
  TrykMap
} from './State';

function getNoAuthLinks(location: { pathname: string; search: string; }): SiteLink[] {
  const noAuthLinks: SiteLink[] = [
    { name: 'Register', url: '/register' }
  ];

  if (location && location.pathname && ['/login', '/logout'].indexOf(location.pathname.toLowerCase()) === -1) {
    noAuthLinks.push({ name: 'Login', url: `/login?returnurl=${encodeURIComponent(location.pathname + location.search)}` });
  } else {
    noAuthLinks.push({ name: 'Login', url: '/login' });
  }

  return noAuthLinks;
}

function getAuthLinks(isCmsUser: boolean): SiteLink[] {
  const links = [
    { name: 'My Account', url: '/account' },
    { name: 'Logout', url: '/logout' }
  ];

  if (!isCmsUser) {
    return links;
  }

  links.splice(0, 0, { name: 'Admin', url: '/admin' });

  return links;
}

export const isAccountLoadingSelector = (state: RootState) => state.user.account.loading;

export const isCmsUserSelector = createSelector(
  (state: RootState) => state.user.status.loggedIn,
  (state: RootState) => state.user.account.data,
  (isLoggedIn, account) => {
    if (!isLoggedIn || !account) {
      return false;
    }

    return !!account.roles.find(
      role => !!['CmsUser', 'CmsSuperUser'].find(x => role === x)
    );
  }
);

export const authLinksSelector = createSelector(
  isAccountLoadingSelector,
  (state: RootState) => state.user.status.loggedIn,
  isCmsUserSelector,
  (state: RootState, props: RouteComponentProps<{}>) => props,
  (loading, isLoggedIn, isCmsUser, props): SiteLink[] => {
    if (loading) {
      return [];
    }

    if (isLoggedIn) {
      return getAuthLinks(isCmsUser);
    } else {
      return getNoAuthLinks(props.location);
    }
  }
);

export type PagerStatus = {
  currPageIdx: number;
  pageSize: number;
  totalPages: number;
  resultCount: number;
  loaded: boolean;
};

export type LoadStatus = {
  loading: boolean;
  failed: boolean;
};

// See more: https://github.com/reactjs/reselect#accessing-react-props-in-selectors
type OrderRouterProps = {
  match: {
    params: { orderId: string }
  }
};

const tryksByOrderStateSelector = (state: RootState, props: OrderRouterProps) => {
  return state.user.tryks.orderMap[props.match.params.orderId];
};

const orderSelector = (state: RootState, props: OrderRouterProps) => {
  return state.user.orders.byId[props.match.params.orderId];
};

export const orderDetailsStatusSelector = createSelector(
  tryksByOrderStateSelector,
  (order: TryksByOrderState): LoadStatus => {
    return {
      loading: order && order.loading || false,
      failed: order && order.failed || false
    };
  }
);

export type OrderDetails = {
  order: TrykApi.Catalog.OrderHistory.IOrderOverview;
  tryks: TrykApi.Catalog.OrderHistory.ITrykOverview[];
};

export const orderDetailsSelector = createSelector(
  orderSelector,
  tryksByOrderStateSelector,
  (state: RootState) => state.user.tryks.itemsById,
  (order: TrykApi.Catalog.OrderHistory.IOrderOverview, tryksByOrder: TryksByOrderState, trykMap: TrykMap): OrderDetails => {
    return {
      order: order,
      tryks: tryksByOrder && tryksByOrder.trykIds.map(x => trykMap[x]) || []
    };
  }
);

type TrykRouterProps = {
  match?: {
    params: { trykId: string }
  }
};

export const trykSelector = (state: RootState, { match }: TrykRouterProps) => {
  return state.user.tryks.itemsById[match.params.trykId];
};

export const trykStatusSelector = (state: RootState, { match }: TrykRouterProps): LoadStatus => {
  return {
    loading: state.user.tryks.loadingById[match.params.trykId] || false,
    failed: state.user.tryks.errorsById[match.params.trykId] || false
  };
};

export const trykDestinationsSelector = (state: RootState, { match }: TrykRouterProps): TrykApi.Catalog.OrderHistory.ITrykDestinationDetail => {
  return state.user.tryks.destinationsById[match.params.trykId] || {
    trykId: parseInt(match.params.trykId, 10),
    emailDestinations: [],
    physicalDestinations: []
  };
};

export const trykDesignsSelector = (state: RootState, props: TrykRouterProps) => {
  return state.user.tryks.designsById[props.match.params.trykId] || [];
};
